import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Seq } from 'immutable';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  LocationViewModel_GetLocationHazardIdsByLocationId,
  LocationViewModel_GetLocationHazardIdsByLocationIdVariables,
} from 'src/app/shared/graphql/graphql-request-types';
import {
  LocationEntityFetchService,
  LocationHazardEntityFetchService,
} from 'src/store/generated/client-sync.generated';
import { selectLocationHazardEntitiesByLocationId } from 'src/store/generated/location-hazard-record.generated';
import {
  LocationProps,
  LocationViewModelBase,
} from 'src/store/generated/location-record.generated';
import { QueryOptions } from 'src/store/fetch-services';
import { LocationHazardViewModel } from './location-hazard-view-model';
import { TimezoneViewModel, TimezoneViewModelService } from './timezone-view-model';

export class LocationViewModel extends LocationViewModelBase {
  public hazards$: Observable<Seq.Indexed<LocationHazardViewModel>>;
  public timezone$: Observable<TimezoneViewModel | undefined>;

  constructor(
    location: LocationProps,
    timezoneService: TimezoneViewModelService,
    locationHazards: LocationHazardEntityFetchService,
    apollo: Apollo,
  ) {
    super(location);
    this.timezone$ =
      this.timezoneCd != null ? timezoneService.getTimezoneByCode(this.timezoneCd) : of(undefined);
    this.hazards$ = locationHazards
      .getMany({
        cacheSelector: selectLocationHazardEntitiesByLocationId(this.id),
        getIdsFallback: fetchLocationHazardIdsByLocationId(this.id, apollo),
      })
      .pipe(
        map((hazards) => {
          return hazards.map((h) => new LocationHazardViewModel(h, this));
        }),
      );
  }
}

function fetchLocationHazardIdsByLocationId(
  locationId: string,
  apollo: Apollo,
): Observable<string[]> {
  return apollo
    .query<
      LocationViewModel_GetLocationHazardIdsByLocationId,
      LocationViewModel_GetLocationHazardIdsByLocationIdVariables
    >({
      query: gql`
        query LocationViewModel_GetLocationHazardIdsByLocationId($locationId: ID!) {
          locations {
            byId(id: $locationId) {
              locationHazards {
                id
              }
            }
          }
        }
      `,
      variables: {
        locationId,
      },
    })
    .pipe(
      map((r) => {
        const loc = r.data.locations.byId;
        const hazardIds = loc != null ? loc.locationHazards.map((l) => l.id) : [];
        return hazardIds;
      }),
    );
}

@Injectable({ providedIn: 'root' })
export class LocationViewModelService {
  constructor(
    private readonly _locationEntityFetchService: LocationEntityFetchService,
    private readonly _timezoneService: TimezoneViewModelService,
    private readonly _locationHazards: LocationHazardEntityFetchService,
    private readonly _apollo: Apollo,
  ) {}

  public getById(id: string, options?: QueryOptions): Observable<LocationViewModel | undefined> {
    return this._locationEntityFetchService.getById(id, options).pipe(
      map((location) => {
        if (location == null) {
          return undefined;
        }

        return new LocationViewModel(
          location,
          this._timezoneService,
          this._locationHazards,
          this._apollo,
        );
      }),
    );
  }
}
